/**
 * Supabase Data Provider — wraps existing Supabase client
 * into the DataProvider interface so it can serve as a fallback.
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  DataProvider,
  GetListParams,
  GetListResult,
  GetOneParams,
  GetOneResult,
  CreateParams,
  CreateResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
  DeleteManyParams,
  DeleteManyResult,
  UpdateManyParams,
  GetOptionResult,
  SetOptionResult,
} from './types';

/** Dynamic table access — bypasses strict table-name typing */
const db = (name: string) => (supabase as any).from(name);

/**
 * Map a resource name to the Supabase table name.
 * Most resource names match their table names directly.
 * Only add entries here when the resource name differs from the table name.
 */
const TABLE_MAP: Record<string, string> = {
  // Aliases for convenience — resource name → actual DB table
  posts: 'blog_posts',
  reviews: 'google_reviews',
  team: 'team_members',
  plans: 'maintenance_plans',
  forms: 'custom_forms',
  submissions: 'custom_form_submissions',
  formFields: 'custom_form_fields',
  competitors: 'competitor_profiles',
  locations: 'business_locations',
  settings: 'business_settings',
  credits: 'ai_credits',
  usageLogs: 'ai_usage_logs',
  activity: 'activity_log',
  themeConfig: 'cms_theme_config',
  projects: 'featured_projects',
  geoKeywords: 'geo_tracked_keywords',
  geoMentions: 'geo_brand_mentions',
  geoSnapshots: 'geo_visibility_snapshots',
  gbpPosts: 'gbp_posts',
  gbpAnalytics: 'gbp_analytics_snapshots',
  seoSnapshots: 'seo_snapshots',
  brokenLinks: 'broken_links',
  linkAudits: 'link_audit_runs',
  autoBlogRuns: 'auto_blog_runs',
  topicalRuns: 'topical_authority_runs',
  slPages: 'service_location_pages',
  locationServices: 'location_services',
  promoAssignments: 'promotion_assignments',
  blogAuthors: 'blog_authors',
  gaConnections: 'ga_connections',
  equipment: 'equipment_pricing',
  creditAdjustments: 'credit_adjustments',
  checklistStates: 'checklist_states',
  contentPrompts: 'content_prompts',
  brandIntelligence: 'brand_intelligence',
  localIntelligence: 'local_intelligence',
  userRoles: 'user_roles',
  siteContent: 'site_content',
  newsletterSubscribers: 'newsletter_subscribers',
  leadActivities: 'lead_activities',
  leadInteractions: 'lead_interactions',
  jobApplications: 'job_applications',
};

/** Resources stored as JSON columns inside `site_content` */
const SITE_CONTENT_COLUMN: Record<string, string> = {
  testimonials: 'testimonials',
  faq: 'faq',
  serviceItems: 'services',
  serviceAreas: 'service_areas',
  offers: 'offers',
  differentiators: 'differentiators',
  processSteps: 'process_steps',
};

const OPTION_TABLE = 'business_settings';

const resolveTable = (resource: string): string =>
  TABLE_MAP[resource] ?? resource.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

const isSiteContentResource = (resource: string): boolean =>
  resource in SITE_CONTENT_COLUMN;

export const supabaseDataProvider: DataProvider = {
  async getList<T = Record<string, unknown>>(
    resource: string,
    params?: GetListParams,
  ): Promise<GetListResult<T>> {
    if (isSiteContentResource(resource)) {
      const col = SITE_CONTENT_COLUMN[resource]!;
      const { data, error } = await db('site_content')
        .select(col)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const arr = Array.isArray(data?.[col]) ? data[col] : [];
      return { data: arr as T[], total: arr.length };
    }

    const tbl = resolveTable(resource);
    let query = db(tbl).select('*', { count: 'exact' });

    if (params?.sort) {
      query = query.order(params.sort.field, { ascending: params.sort.order === 'ASC' });
    }
    if (params?.pagination) {
      const { page, perPage } = params.pagination;
      const from = (page - 1) * perPage;
      query = query.range(from, from + perPage - 1);
    }
    if (params?.filter) {
      for (const [k, v] of Object.entries(params.filter)) {
        if (v !== undefined && v !== null) query = query.eq(k, String(v));
      }
    }

    const { data, error, count } = await query;
    if (error) throw error;
    return { data: (data ?? []) as T[], total: count ?? data?.length ?? 0 };
  },

  async getOne<T = Record<string, unknown>>(
    resource: string,
    params: GetOneParams,
  ): Promise<GetOneResult<T>> {
    const tbl = resolveTable(resource);
    const { data, error } = await db(tbl)
      .select('*')
      .eq('id', String(params.id))
      .single();
    if (error) throw error;
    return { data: data as T };
  },

  async create<T = Record<string, unknown>>(
    resource: string,
    params: CreateParams,
  ): Promise<CreateResult<T>> {
    const tbl = resolveTable(resource);
    const { data, error } = await db(tbl)
      .insert(params.data)
      .select()
      .single();
    if (error) throw error;
    return { data: data as T };
  },

  async update<T = Record<string, unknown>>(
    resource: string,
    params: UpdateParams,
  ): Promise<UpdateResult<T>> {
    const tbl = resolveTable(resource);
    const { data, error } = await db(tbl)
      .update(params.data)
      .eq('id', String(params.id))
      .select()
      .single();
    if (error) throw error;
    return { data: data as T };
  },

  async delete<T = Record<string, unknown>>(
    resource: string,
    params: DeleteParams,
  ): Promise<DeleteResult<T>> {
    const tbl = resolveTable(resource);
    const { data, error } = await db(tbl)
      .delete()
      .eq('id', String(params.id))
      .select()
      .single();
    if (error) throw error;
    return { data: data as T };
  },

  async deleteMany(
    resource: string,
    params: DeleteManyParams,
  ): Promise<DeleteManyResult> {
    const tbl = resolveTable(resource);
    const { error, count } = await db(tbl)
      .delete()
      .in('id', params.ids.map(String));
    if (error) throw error;
    return { deleted: count || 0 };
  },

  async updateMany<T = Record<string, unknown>>(
    resource: string,
    params: UpdateManyParams<T>,
  ): Promise<{ updated: number }> {
    const tbl = resolveTable(resource);
    const { error, count } = await db(tbl)
      .update(params.data)
      .in('id', params.ids.map(String));
    if (error) throw error;
    return { updated: count || 0 };
  },

  async getOption<T = unknown>(name: string): Promise<GetOptionResult<T>> {
    const { data, error } = await db(OPTION_TABLE)
      .select('*')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const value = data?.[name] ?? data;
    return { data: value as T };
  },

  async setOption<T = unknown>(name: string, value: T): Promise<SetOptionResult<T>> {
    const { data: existing } = await db(OPTION_TABLE)
      .select('id')
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (existing?.id) {
      const { error } = await db(OPTION_TABLE)
        .update({ [name]: value })
        .eq('id', existing.id);
      if (error) throw error;
    }
    return { data: value as T };
  },
};
